import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  booleanAttribute,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DsIconComponent } from '../icons/ds-icon.component';
import type { DsIconName } from '../icons/ds-icons';

export type DsNavigationCardAppearance = 'outlined' | 'filled';
export type DsNavigationCardLayout = 'stacked' | 'inline';
export type DsNavigationCardSize = 's' | 'm' | 'l';
export type DsNavigationCardState = 'default' | 'hover' | 'pressed' | 'focus' | 'disabled';
export type DsNavigationCardShadow = 'none' | 'l1' | 'l2' | 'l3';

@Component({
  selector: 'ds-navigation-card',
  standalone: true,
  imports: [NgTemplateOutlet, DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (href) {
      <a
        [class]="hostClasses"
        [style.--ds-navigation-card-resolved-background]="resolvedBackground"
        [style.--ds-navigation-card-resolved-border-color]="resolvedBorderColor"
        [style.--ds-navigation-card-resolved-headline-color]="resolvedHeadlineColor"
        [style.--ds-navigation-card-resolved-eyebrow-color]="resolvedEyebrowColor"
        [style.--ds-navigation-card-resolved-supporting-text-color]="resolvedSupportingTextColor"
        [style.--ds-navigation-card-resolved-leading-color]="resolvedLeadingColor"
        [style.--ds-navigation-card-resolved-trailing-icon-color]="resolvedTrailingIconColor"
        [attr.href]="isDisabled ? null : href"
        [attr.target]="target || null"
        [attr.rel]="rel || null"
        [attr.tabindex]="isDisabled ? -1 : null"
        [attr.aria-disabled]="isDisabled ? 'true' : null"
        [attr.aria-label]="ariaLabel || null"
        (click)="handleActivation($event)"
      >
        <ng-container [ngTemplateOutlet]="cardContent" />
      </a>
    } @else {
      <button
        type="button"
        [class]="hostClasses"
        [style.--ds-navigation-card-resolved-background]="resolvedBackground"
        [style.--ds-navigation-card-resolved-border-color]="resolvedBorderColor"
        [style.--ds-navigation-card-resolved-headline-color]="resolvedHeadlineColor"
        [style.--ds-navigation-card-resolved-eyebrow-color]="resolvedEyebrowColor"
        [style.--ds-navigation-card-resolved-supporting-text-color]="resolvedSupportingTextColor"
        [style.--ds-navigation-card-resolved-leading-color]="resolvedLeadingColor"
        [style.--ds-navigation-card-resolved-trailing-icon-color]="resolvedTrailingIconColor"
        [disabled]="isDisabled"
        [attr.aria-label]="ariaLabel || null"
        (click)="handleActivation($event)"
      >
        <ng-container [ngTemplateOutlet]="cardContent" />
      </button>
    }

    <ng-template #cardContent>
      <span class="ds-navigation-card__interaction-overlay" aria-hidden="true"></span>
      <span class="ds-navigation-card__inner">
        <span class="ds-navigation-card__leading" aria-hidden="true">
          @if (leadingIcon) {
            <ds-icon
              [name]="leadingIcon"
              size="var(--ds-navigation-card-leading-size)"
            />
          } @else {
            <ng-content select="[navigationCardLeading]" />
          }
        </span>

        <span
          class="ds-navigation-card__content"
          [class.ds-navigation-card__content--inline]="layout === 'inline'"
        >
          @if (layout === 'inline') {
            @if (eyebrow) {
              <span class="ds-navigation-card__inline-label">
                <ng-container
                  [ngTemplateOutlet]="formattedText"
                  [ngTemplateOutletContext]="{ $implicit: eyebrow }"
                />
              </span>
            } @else {
              <span class="ds-navigation-card__inline-spacer" aria-hidden="true"></span>
            }
            <span class="ds-navigation-card__inline-value">
              <ng-container
                [ngTemplateOutlet]="formattedText"
                [ngTemplateOutletContext]="{ $implicit: headline }"
              />
            </span>
          } @else {
            @if (eyebrow) {
              <span class="ds-navigation-card__eyebrow">
                <ng-container
                  [ngTemplateOutlet]="formattedText"
                  [ngTemplateOutletContext]="{ $implicit: eyebrow }"
                />
              </span>
            }
            <span class="ds-navigation-card__headline">
              <ng-container
                [ngTemplateOutlet]="formattedText"
                [ngTemplateOutletContext]="{ $implicit: headline }"
              />
            </span>
            @if (supportingText) {
              <span class="ds-navigation-card__supporting-text">
                <ng-container
                  [ngTemplateOutlet]="formattedText"
                  [ngTemplateOutletContext]="{ $implicit: supportingText }"
                />
              </span>
            }
          }
        </span>

        <span class="ds-navigation-card__trailing" aria-hidden="true">
          <ds-icon
            name="KeyboardArrowRight"
            size="var(--ds-navigation-card-trailing-icon-size)"
          />
        </span>
      </span>
    </ng-template>

    <ng-template #formattedText let-text>
      @for (part of textParts(text); track $index) {
        @if (part.bold) {
          <strong>{{ part.text }}</strong>
        } @else {
          <ng-container>{{ part.text }}</ng-container>
        }
      }
    </ng-template>
  `,
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }

    .ds-navigation-card {
      appearance: none;
      position: relative;
      display: flex;
      width: 100%;
      min-width: 0;
      min-height: var(--ds-navigation-card-min-height);
      margin: 0;
      padding: var(--ds-navigation-card-padding-y) var(--ds-navigation-card-padding-x);
      overflow: hidden;
      background: var(--ds-navigation-card-resolved-background);
      border: var(--ds-navigation-card-border-width) solid transparent;
      border-radius: var(--ds-navigation-card-border-radius);
      box-sizing: border-box;
      color: var(--ds-navigation-card-resolved-headline-color);
      font-family: var(--ds-foundation-font-family-primary, 'Sura Sans', system-ui, sans-serif);
      text-align: start;
      text-decoration: none;
      transform: scale(1);
      transform-origin: center;
      cursor: pointer;
      outline: none;
      transition:
        transform var(--ds-navigation-card-transition-duration) ease,
        opacity var(--ds-navigation-card-transition-duration) ease,
        box-shadow var(--ds-navigation-card-transition-duration) ease;
    }

    .ds-navigation-card--bordered {
      border-color: var(--ds-navigation-card-resolved-border-color);
    }

    .ds-navigation-card--borderless {
      border-color: transparent;
    }

    .ds-navigation-card--shadow-l1 {
      box-shadow: var(--ds-shadow-l1);
    }

    .ds-navigation-card--shadow-l2 {
      box-shadow: var(--ds-shadow-l2);
    }

    .ds-navigation-card--shadow-l3 {
      box-shadow: var(--ds-shadow-l3);
    }

    .ds-navigation-card--shadow-none {
      box-shadow: none;
    }

    .ds-navigation-card__interaction-overlay {
      position: absolute;
      inset: 0;
      z-index: 0;
      border-radius: inherit;
      background: var(--ds-navigation-card-hover-overlay);
      opacity: 0;
      pointer-events: none;
      transition:
        background-color var(--ds-navigation-card-transition-duration) ease,
        opacity var(--ds-navigation-card-transition-duration) ease;
    }

    .ds-navigation-card:hover:not(.ds-navigation-card--disabled),
    .ds-navigation-card--hover:not(.ds-navigation-card--disabled),
    .ds-navigation-card:active:not(.ds-navigation-card--disabled),
    .ds-navigation-card--pressed:not(.ds-navigation-card--disabled) {
      transform: scale(var(--ds-navigation-card-interaction-scale));
    }

    .ds-navigation-card:hover:not(.ds-navigation-card--disabled)
      .ds-navigation-card__interaction-overlay,
    .ds-navigation-card--hover:not(.ds-navigation-card--disabled)
      .ds-navigation-card__interaction-overlay {
      opacity: 1;
    }

    .ds-navigation-card:active:not(.ds-navigation-card--disabled)
      .ds-navigation-card__interaction-overlay,
    .ds-navigation-card--pressed:not(.ds-navigation-card--disabled)
      .ds-navigation-card__interaction-overlay {
      background: var(--ds-navigation-card-pressed-overlay);
      opacity: 1;
    }

    .ds-navigation-card:focus-visible:not(.ds-navigation-card--disabled),
    .ds-navigation-card--focus:not(.ds-navigation-card--disabled) {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-navigation-card--disabled {
      opacity: calc(var(--ds-navigation-card-disabled-opacity) / 100px);
      cursor: not-allowed;
    }

    .ds-navigation-card__inner {
      position: relative;
      z-index: 1;
      display: flex;
      flex: 1 1 auto;
      align-items: center;
      width: 100%;
      min-width: 0;
    }

    .ds-navigation-card__leading {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--ds-navigation-card-leading-size);
      height: var(--ds-navigation-card-leading-size);
      margin-inline-end: var(--ds-navigation-card-leading-gap);
      flex: 0 0 var(--ds-navigation-card-leading-size);
      overflow: hidden;
      color: var(--ds-navigation-card-resolved-leading-color);
    }

    .ds-navigation-card__leading:empty {
      display: none;
    }

    .ds-navigation-card__content {
      display: flex;
      flex: 1 1 0;
      flex-direction: column;
      gap: var(--ds-navigation-card-content-gap);
      min-width: 0;
    }

    .ds-navigation-card__content--inline {
      flex-direction: row;
      align-items: center;
    }

    .ds-navigation-card strong {
      font-weight: var(--ds-navigation-card-emphasis-font-weight);
    }

    .ds-navigation-card__eyebrow {
      overflow: hidden;
      color: var(--ds-navigation-card-resolved-eyebrow-color);
      font-size: var(--ds-navigation-card-m-eyebrow-font-size);
      font-weight: var(--ds-navigation-card-eyebrow-font-weight);
      line-height: var(--ds-navigation-card-m-eyebrow-line-height);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-navigation-card__headline {
      display: -webkit-box;
      overflow: hidden;
      color: var(--ds-navigation-card-resolved-headline-color);
      font-size: var(--ds-navigation-card-m-headline-font-size);
      font-weight: var(--ds-navigation-card-headline-font-weight);
      line-height: var(--ds-navigation-card-m-headline-line-height);
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }

    .ds-navigation-card__supporting-text {
      display: -webkit-box;
      overflow: hidden;
      color: var(--ds-navigation-card-resolved-supporting-text-color);
      font-size: var(--ds-navigation-card-m-supporting-text-font-size);
      font-weight: var(--ds-navigation-card-supporting-text-font-weight);
      line-height: var(--ds-navigation-card-m-supporting-text-line-height);
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }

    .ds-navigation-card__inline-label {
      flex: 1 1 0;
      min-width: 0;
      overflow: hidden;
      color: var(--ds-navigation-card-resolved-eyebrow-color);
      font-size: var(--ds-navigation-card-m-eyebrow-font-size);
      font-weight: var(--ds-navigation-card-eyebrow-font-weight);
      line-height: var(--ds-navigation-card-m-eyebrow-line-height);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-navigation-card__inline-spacer {
      flex: 1 1 0;
    }

    .ds-navigation-card__inline-value {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      color: var(--ds-navigation-card-resolved-headline-color);
      font-size: var(--ds-navigation-card-m-headline-font-size);
      font-weight: var(--ds-navigation-card-headline-font-weight);
      line-height: var(--ds-navigation-card-m-headline-line-height);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-navigation-card--size-s .ds-navigation-card__eyebrow,
    .ds-navigation-card--size-s .ds-navigation-card__inline-label {
      font-size: var(--ds-navigation-card-s-eyebrow-font-size);
      line-height: var(--ds-navigation-card-s-eyebrow-line-height);
    }

    .ds-navigation-card--size-s .ds-navigation-card__headline,
    .ds-navigation-card--size-s .ds-navigation-card__inline-value {
      font-size: var(--ds-navigation-card-s-headline-font-size);
      line-height: var(--ds-navigation-card-s-headline-line-height);
    }

    .ds-navigation-card--size-s .ds-navigation-card__supporting-text {
      font-size: var(--ds-navigation-card-s-supporting-text-font-size);
      line-height: var(--ds-navigation-card-s-supporting-text-line-height);
    }

    .ds-navigation-card--size-l .ds-navigation-card__eyebrow,
    .ds-navigation-card--size-l .ds-navigation-card__inline-label {
      font-size: var(--ds-navigation-card-l-eyebrow-font-size);
      line-height: var(--ds-navigation-card-l-eyebrow-line-height);
    }

    .ds-navigation-card--size-l .ds-navigation-card__headline,
    .ds-navigation-card--size-l .ds-navigation-card__inline-value {
      font-size: var(--ds-navigation-card-l-headline-font-size);
      line-height: var(--ds-navigation-card-l-headline-line-height);
    }

    .ds-navigation-card--size-l .ds-navigation-card__supporting-text {
      font-size: var(--ds-navigation-card-l-supporting-text-font-size);
      line-height: var(--ds-navigation-card-l-supporting-text-line-height);
    }

    .ds-navigation-card__trailing {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--ds-navigation-card-trailing-icon-size);
      height: var(--ds-navigation-card-trailing-icon-size);
      margin-inline-start: var(--ds-navigation-card-trailing-gap);
      flex: 0 0 var(--ds-navigation-card-trailing-icon-size);
      color: var(--ds-navigation-card-resolved-trailing-icon-color);
    }
  `],
})
export class DsNavigationCardComponent {
  /** Plain text. Wrap any bold phrases in **double asterisks**. */
  @Input({ required: true }) headline = '';
  /** Plain text. Wrap any bold phrases in **double asterisks**. */
  @Input() eyebrow = '';
  /** Plain text. Wrap any bold phrases in **double asterisks**. */
  @Input() supportingText = '';
  @Input() layout: DsNavigationCardLayout = 'stacked';
  @Input() size: DsNavigationCardSize = 'm';
  @Input() appearance: DsNavigationCardAppearance = 'outlined';
  @Input() state: DsNavigationCardState = 'default';
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() leadingIcon: DsIconName | string = '';
  @Input() href = '';
  @Input() target = '';
  @Input() rel = '';
  @Input() ariaLabel = '';
  @Input() background: string | null = null;
  @Input() border: boolean | string | null = null;
  @Input() borderColor: string | null = null;
  @Input() shadow: DsNavigationCardShadow = 'none';
  @Input() headlineColor: string | null = null;
  @Input() eyebrowColor: string | null = null;
  @Input() supportingTextColor: string | null = null;
  @Input() leadingColor: string | null = null;
  @Input() trailingIconColor: string | null = null;

  @Output() clicked = new EventEmitter<MouseEvent>();

  get isDisabled(): boolean {
    return this.disabled || this.state === 'disabled';
  }

  get resolvedBackground(): string {
    return this.background ?? (this.appearance === 'filled'
      ? 'var(--ds-navigation-card-filled-background)'
      : 'var(--ds-navigation-card-outlined-background)');
  }

  get showBorder(): boolean {
    if (this.border === null || this.border === undefined) {
      return this.appearance === 'outlined';
    }
    return this.border === true || this.border === '' || this.border === 'true';
  }

  get resolvedBorderColor(): string {
    return this.borderColor ?? 'var(--ds-navigation-card-outlined-border-color)';
  }

  get resolvedHeadlineColor(): string {
    return this.headlineColor ?? 'var(--ds-navigation-card-headline-color)';
  }

  get resolvedEyebrowColor(): string {
    return this.eyebrowColor ?? 'var(--ds-navigation-card-eyebrow-color)';
  }

  get resolvedSupportingTextColor(): string {
    return this.supportingTextColor ?? 'var(--ds-navigation-card-eyebrow-color)';
  }

  get resolvedLeadingColor(): string {
    return this.leadingColor ?? 'var(--ds-navigation-card-leading-color)';
  }

  get resolvedTrailingIconColor(): string {
    return this.trailingIconColor ?? 'var(--ds-navigation-card-trailing-icon-color)';
  }

  get hostClasses(): string {
    return [
      'ds-navigation-card',
      `ds-navigation-card--${this.appearance}`,
      `ds-navigation-card--${this.state}`,
      `ds-navigation-card--size-${this.size}`,
      this.showBorder ? 'ds-navigation-card--bordered' : 'ds-navigation-card--borderless',
      `ds-navigation-card--shadow-${this.shadow}`,
      this.isDisabled ? 'ds-navigation-card--disabled' : '',
    ].filter(Boolean).join(' ');
  }

  textParts(text: string): { text: string; bold: boolean }[] {
    const parts: { text: string; bold: boolean }[] = [];
    let cursor = 0;
    while (cursor < text.length) {
      const start = text.indexOf('**', cursor);
      const end = start < 0 ? -1 : text.indexOf('**', start + 2);
      if (end < 0) {
        parts.push({ text: text.slice(cursor), bold: false });
        break;
      }
      if (start > cursor) parts.push({ text: text.slice(cursor, start), bold: false });
      const bold = end > start + 2;
      parts.push({ text: bold ? text.slice(start + 2, end) : '****', bold });
      cursor = end + 2;
    }
    return parts;
  }

  handleActivation(event: MouseEvent): void {
    if (this.isDisabled) {
      event.preventDefault();
      return;
    }

    this.clicked.emit(event);
  }
}
